import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supaFetch, getValidToken } from '../lib/supabase.js';
import { queryKeys } from '../lib/queryKeys.js';
import { uid } from '../lib/utils.js';
import { SUPABASE_URL, SUPABASE_KEY } from '../lib/constants.js';

// ─── callNotifyFunction ───────────────────────────────────────────────────────
// Best-effort: fires the approval email but does NOT throw if it fails.
// The approval itself is already committed; email is a nice-to-have.
async function callNotifyFunction({ email, name, company, token, tokenType, preferred_language = 'ar' }) {
  try {
    const authToken = await getValidToken();
    await fetch(`${SUPABASE_URL}/functions/v1/notify-retailer-approval`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'apikey': SUPABASE_KEY,
        ...(authToken ? { 'Authorization': `Bearer ${authToken}` } : {}),
      },
      body: JSON.stringify({ email, name, company, token, tokenType, preferred_language }),
    });
  } catch (err) {
    // Log but never propagate — email failure must not block the approval UI
    console.warn('[notify-retailer-approval] email send failed (non-fatal):', err.message);
  }
}

// ─── upsertApprovedLeadToCustomers ──────────────────────────────────────────
// Best-effort: captures the approved applicant in the CRM the instant their
// application is approved — before any order is placed.
// Two-stage: try INSERT first; if email already exists, ensure is_subscribed=true.
// Errors are swallowed — this must never block token generation or email sending.
async function upsertApprovedLeadToCustomers({ applicationId, companyName, contactName, email, phone }) {
  try {
    await supaFetch('POST', 'customers', {
      id:            uid(),
      business_name: companyName  || contactName || '',
      contact_name:  contactName  || '',
      email:         email        || '',
      phone:         phone        || '',
      source:        'approved_lead',
      is_subscribed: true,
      // Exact link to the account, so the CRM row resolves to a catalogue
      // token without falling back to email/phone matching.
      retailer_application_id: applicationId || null,
      created_at:    new Date().toISOString(),
    }, '', { 'Prefer': 'return=minimal' });
  } catch {
    // Email conflict or other constraint — best-effort: flag them as subscribed
    // and backfill the account link on the row that already exists.
    try {
      if (email) {
        await supaFetch('PATCH', 'customers',
          { is_subscribed: true, ...(applicationId ? { retailer_application_id: applicationId } : {}) },
          `?email=eq.${encodeURIComponent(email)}`);
      }
    } catch { /* completely silent */ }
  }
}

// ─── useApplications ──────────────────────────────────────────────────────────
// Fetches retailer applications filtered by status.
// Embeds catalogue_tokens via the retailer_application_id FK so the UI can show
// token status (🔗 active / no link) per application without a separate query.
export function useApplications({ status = 'pending' } = {}) {
  // expires_at is part of the embed so the UI can tell a usable token from an
  // active-but-expired one (the "View as customer" link depends on this).
  const base = 'select=*,catalogue_tokens(token,type,active,created_at,expires_at)&order=created_at.desc';
  const query = status === 'all'
    ? `?${base}`
    : `?status=eq.${encodeURIComponent(status)}&${base}`;

  return useQuery({
    queryKey:  [...queryKeys.admin.applications({ status }), 'withTokens'],
    queryFn:   () => supaFetch('GET', 'retailer_applications', null, query),
    select:    (result) => result.data ?? [],
    staleTime: 5 * 60 * 1000,
    gcTime:    10 * 60 * 1000,
  });
}

// ─── useApproveApplication ────────────────────────────────────────────────────
// Three-step mutation:
//   1. POST a new catalogue_tokens row linked to the application (Phase 7)
//   2. PATCH the application status to 'approved'
//   3. Send the approval email (best-effort)
export function useApproveApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ applicationId, applicantEmail, applicantName, applicantPhone, companyName, tokenType, preferredLanguage }) => {
      const token = uid() + uid();

      // 1. Create the catalogue token — Phase 7: set retailer_application_id FK
      //    so useTokenProfile can join back to the retailer's profile.
      await supaFetch('POST', 'catalogue_tokens', {
        token,
        label:                   `${companyName || applicantName} — ${applicantEmail}`,
        type:                    tokenType,  // 'standard' | 'vat'
        active:                  true,
        retailer_application_id: applicationId,
      }, '', { 'Prefer': 'return=minimal' });

      // 2. Mark the application as approved
      await supaFetch(
        'PATCH',
        'retailer_applications',
        { status: 'approved' },
        `?id=eq.${applicationId}`
      );

      // 3. Upsert into CRM as an approved lead (best-effort — does not throw)
      await upsertApprovedLeadToCustomers({
        applicationId,
        companyName,
        contactName: applicantName,
        email:       applicantEmail,
        phone:       applicantPhone,
      });

      // 4. Send the approval email (best-effort — does not throw)
      await callNotifyFunction({
        email:              applicantEmail,
        name:               applicantName,
        company:            companyName || applicantName,
        token,
        tokenType,
        preferred_language: preferredLanguage || 'ar',
      });

      return { token };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.applicationsRoot() });
    },

    onError: (err) => {
      console.error('[useApproveApplication] mutation failed:', err.message);
    },
  });
}

// ─── useCreateTokenForApproved ────────────────────────────────────────────────
// Creates a fresh catalogue token for an already-approved application.
// Does NOT change the application status (it's already 'approved').
// Sends the approval email with the new link — best-effort.
export function useCreateTokenForApproved() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ applicationId, applicantEmail, applicantName, applicantPhone, companyName, tokenType, preferredLanguage }) => {
      const token = uid() + uid();

      await supaFetch('POST', 'catalogue_tokens', {
        token,
        label:                   `${companyName || applicantName} — ${applicantEmail}`,
        type:                    tokenType,
        active:                  true,
        retailer_application_id: applicationId,
      }, '', { 'Prefer': 'return=minimal' });

      // Upsert CRM record (idempotent)
      await upsertApprovedLeadToCustomers({
        applicationId,
        companyName,
        contactName: applicantName,
        email:       applicantEmail,
        phone:       applicantPhone,
      });

      // Send link via email (best-effort)
      await callNotifyFunction({
        email:              applicantEmail,
        name:               applicantName,
        company:            companyName || applicantName,
        token,
        tokenType,
        preferred_language: preferredLanguage || 'ar',
      });

      return { token };
    },

    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.applicationsRoot() });
    },
  });
}

// ─── useToggleExport ─────────────────────────────────────────────────────────
// Flips can_export_data for an approved application. Staff-only (JWT enforced by RLS).
export function useToggleExport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ applicationId, value }) =>
      supaFetch('PATCH', 'retailer_applications', { can_export_data: value }, `?id=eq.${applicationId}`, { 'Prefer': 'return=minimal' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.applicationsRoot() });
    },
  });
}

// ─── useRejectApplication ─────────────────────────────────────────────────────
export function useRejectApplication() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ applicationId }) =>
      supaFetch(
        'PATCH',
        'retailer_applications',
        { status: 'rejected' },
        `?id=eq.${applicationId}`
      ),

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.applicationsRoot() });
    },
  });
}
